// Corporate governance sample data and helpers, shared by Governance.tsx and its modals (no hub API exists yet)

export interface GovSection {
    id: string;
    // the short name in the Section column, the upload modal and Manage sections ("Policies")
    name: string;
    // the long name on the filter chip and in the edit modal ("Policies & Procedures"); a rename sets both
    label: string;
}

export interface GovDoc {
    id: string;
    name: string;
    section: string;
    file: string;
    // "PDF", "DOCX"…
    kind: string;
    sizeKb: number;
    // ISO date shown as "Updated"
    updated: string;
    visible: boolean;
    uploadedBy: string;
}

export const SECTIONS: GovSection[] = [
    { id: 'charters', name: 'Charters', label: 'Charters' },
    { id: 'policies', name: 'Policies', label: 'Policies & Procedures' },
];

const doc = (id: string, name: string, section: string, sizeKb: number, updated: string, visible = true, file?: string): GovDoc =>
    ({ id, name, section, sizeKb, updated, visible, kind: 'PDF', file: file ?? `${name.replace(/&/g, 'and').replace(/[^A-Za-z0-9]+/g, '-')}-2026.pdf`, uploadedBy: 'Lily Ferliansyah' });

// sample documents (spec 405:10406): page 1 of the list
export const DOCS: GovDoc[] = [
    doc('board', 'Board Charter', 'charters', 240, '2026-06-30'),
    doc('audit', 'Audit & Risk Committee Charter', 'charters', 198, '2026-06-30'),
    doc('remco', 'Remuneration & Nomination Committee Charter', 'charters', 176, '2026-06-30'),
    doc('conduct', 'Code of Conduct', 'policies', 310, '2026-02-12'),
    doc('disclosure', 'Continuous Disclosure Policy', 'policies', 220, '2026-02-12'),
    doc('trading', 'Securities Trading Policy', 'policies', 205, '2026-02-12', true, 'Securities-Trading-Policy-Sep-2026.pdf'),
    doc('whistle', 'Whistleblower Policy', 'policies', 188, '2026-02-12'),
    doc('diversity', 'Diversity Policy', 'policies', 150, '2026-02-12', false),
];

// the library totals in the spec (12 · Charters 3 · Policies 7) include documents on page 2 that the sample doesn't list
export const OFF_PAGE: Record<string, number> = { all: 4, policies: 2 };

export const OTHER: GovSection = { id: 'other', name: 'Other', label: 'Other' };

export const sizeText = (kb: number) => kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb} KB`;

// "30 Jun 2026" in the table, "30 June 2026" in the edit modal
export const shortDate = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
export const longDate = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
export const todayIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
// typed dates back to ISO; null when it can't be read
export const parseDate = (text: string) => {
    const t = Date.parse(text);
    if (Number.isNaN(t)) return null;
    const d = new Date(t);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// a chosen file as the modals show it
export interface PickedFile { file: string; kind: string; sizeKb: number }
export const pickedFrom = (f: File): PickedFile => ({
    file: f.name,
    kind: (f.name.split('.').pop() || 'file').toUpperCase(),
    sizeKb: Math.max(1, Math.round(f.size / 1024)),
});
// "Securities-Trading-Policy-Sep-2026.pdf" → "Securities Trading Policy" (a trailing month / year is dropped)
export const nameFromFile = (file: string) => file.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ')
    .replace(/(\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*)?\s+(19|20)\d{2}$/i, '').trim();
